import Vue from 'vue'; // initialize
import { plainToClass } from 'class-transformer';
// import localization plugin
import VueI18n from 'vue-i18n';
import userrouter from './router.ts'; // use vue router
import saasrouter from './saas-router.ts'; // use vue router

// import BootstrapVue from 'bootstrap-vue'; //bootstrap components
import { BootstrapVue, BootstrapVueIcons } from 'bootstrap-vue';
import Axios from 'axios'; // use axios to consume APIs
import Vuex from 'vuex';
import moment from 'moment';

import Notifications from 'vue-notification';
import velocity from 'velocity-animate';
import vueHeadful from 'vue-headful';
import { v4 as uuidv4 } from 'uuid';

import * as linkify from 'linkifyjs';
import linkifyHtml from 'linkifyjs/html';
import hashtag from 'linkifyjs/plugins/hashtag';
import mention from 'linkifyjs/plugins/mention';

import * as Sentry from '@sentry/vue';
import { UnleashClient } from 'unleash-proxy-client';

import App from './App.vue';

import 'web-pingjs';

// import bootstrap styling
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue/dist/bootstrap-vue.css';

// import modal fulscreen
import 'bootstrap4-fs-modal/dist/css/bootstrap-fs-modal.min.css';
// import custom styling
import './assets/less/coapp.less';
// import Base components globally
import './components/globals';

import SvgIcon from 'vue-svgicon';
import VuePortal from '@linusborg/vue-simple-portal';

import store from './store';
import EventBus from './eventBus';
import { getPersistedUserLocale, resolveInitialUserLocale, setDocumentLocale } from './utils/locale.js';
import { createToastService } from './utils/toastBus';
const { name: packageName, version: packageVersion } = require('../package.json');
// make Vue use the localization plugin
Vue.use(VueI18n);
// import combined global language files
import { messages, numbers } from './language/locales.js';
// detect client language setting
let browserLanguage = '';
try {
    browserLanguage = navigator.language;
} catch (error) {
    console && console.info ? console.info('could not detect navigator.language') : null;
}
const initialLocale = resolveInitialUserLocale({
    userLocale: store?.state?.me?.Language,
    persistedLocale: getPersistedUserLocale(),
    navigatorLocale: browserLanguage,
});
Vue.prototype.$language = initialLocale;
setDocumentLocale(initialLocale);
// initiate the localization module
const i18n = new VueI18n({
    locale: initialLocale,
    fallbackLocale: 'en',
    silentFallbackWarn: true,
    silentTranslationWarn: true,
    messages: messages,
    numberFormats: numbers,
});

// hashtag(linkify);
mention(linkify);

Vue.component('VueHeadful', vueHeadful);
const VueCookie = require('vue-cookie');

function removeAppTokenFromUrl(rawUrl: string) {
    try {
        const url = new URL(rawUrl, window.location.origin);
        url.searchParams.delete('apptoken');
        return url.toString();
    } catch (error) {
        // Do not prevent Sentry from reporting an event when its URL is not parseable.
        return rawUrl;
    }
}

function removeEmailData(value: unknown): unknown {
    if (typeof value === 'string') {
        // Redact email addresses that could be embedded in error messages or URL values.
        return value.replace(/[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g, '[filtered]');
    }

    if (Array.isArray(value)) {
        return value.map(removeEmailData);
    }

    if (value && typeof value === 'object') {
        const sanitizedValue: Record<string, unknown> = {};

        Object.entries(value as Record<string, unknown>).forEach(([key, nestedValue]) => {
            // Sentry event data may use different casing for email fields.
            if (key.toLowerCase() !== 'email') {
                sanitizedValue[key] = removeEmailData(nestedValue);
            }
        });

        return sanitizedValue;
    }

    return value;
}

// Initialize Sentry before the app mounts so runtime errors are reported with the deployed web version.
Sentry.init({
    Vue,
    dsn: 'https://871b80be3d8b4963a4bc6d2233acbd1e@bugsink.coapp.io/1',
    release: `${packageName}@${packageVersion}`,
    integrations: [],
    tracesSampleRate: 0,
    beforeSend(event) {
        const sanitizedEvent = removeEmailData(event) as typeof event;

        if (sanitizedEvent.request?.url) {
            // Remove the app token from the URL before the error event is sent to Sentry.
            return {
                ...sanitizedEvent,
                request: {
                    ...sanitizedEvent.request,
                    url: removeAppTokenFromUrl(sanitizedEvent.request.url),
                },
            };
        }

        return sanitizedEvent;
    },
});

// Expose a manual test hook so Sentry can be verified from the browser console on demand.
(window as Window & typeof globalThis & { triggerSentryTestError?: () => never }).triggerSentryTestError = () => {
    throw new Error('Sentry test error from community frontend');
};
// Tell Vue to use the plugin

Vue.use(VuePortal);
Vue.use(VueCookie);
Vue.use(Notifications, { velocity });
Vue.use(BootstrapVue);
// Keep Bootstrap icons registered until the remaining product usages have been migrated.
Vue.use(BootstrapVueIcons);
Vue.use(Vuex);
Vue.use(SvgIcon);

Vue.config.productionTip = false;
// ALTCHA is loaded as a browser custom element, not as a Vue component.
Vue.config.ignoredElements = [...(Vue.config.ignoredElements || []), 'altcha-widget'];
// API consumption setup
Vue.prototype.axios = Axios;
// Use the current page origin for API requests so local and hosted environments stay same-site.
Axios.defaults.baseURL = `${window.location.origin}/api/v1`;
Axios.defaults.withCredentials = true;
Axios.defaults.xsrfCookieName = '_csrf';
// Expose the shared toast entry point so views can trigger generic and chat-specific toasts from one API.
Vue.prototype.$coToast = createToastService({
    listUsersByIDs: (payload) => store.dispatch('listUsersByIDs', payload),
    t: (key) => i18n.t(key).toString(),
});

const unleash = new UnleashClient({
    url: 'https://unleash-proxy.coapp.io/proxy',
    clientKey: 'key1-yMqT8noydCDxLmCz8uqzpqYaacvB9bN8aH7BfqtXZ2vLs33fRid6zCz9LLHeoKNX',
    appName: 'coapp-frontend',
});

Vue.prototype.$unleash = unleash;

// set $isMobile flag globally based on screen width
Vue.prototype.$isMobile = window && window.innerWidth <= 992;

// enable touch events handling
import Vue2TouchEvents from 'vue2-touch-events';
Vue.use(Vue2TouchEvents);
// set $isTouchDevice flag globally
Vue.prototype.$isTouchDevice =
    'ontouchstart' in window || navigator.maxTouchPoints > 0 || (navigator as any).msMaxTouchPoints > 0;

function isLocalStorage() {
    const test = 'test';
    try {
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
        // available
    } catch (e) {
        return false;
        // unavailable
    }
}

if (isLocalStorage()) {
    // Cookie-based sessions supersede the legacy browser-stored OAuth token.
    localStorage.removeItem('_authtoken');
    Vue.prototype.$isLocalStorage = true;
} else {
    Vue.prototype.$isLocalStorage = false;
}

async function refreshCsrfToken() {
    const response = await fetch('/bff/session', {
        credentials: 'include',
        method: 'GET',
    });

    return {
        ok: response.ok,
    };
}

function takeAppTokenFromUrl() {
    const url = new URL(window.location.href);
    const appToken = url.searchParams.get('apptoken');

    if (appToken) {
        // Remove this credential before any navigation, analytics, or error reporting can retain it.
        url.searchParams.delete('apptoken');
        window.history.replaceState(window.history.state, document.title, `${url.pathname}${url.search}${url.hash}`);
    }

    return appToken;
}

async function restoreBffSession() {
    const appToken = takeAppTokenFromUrl();

    try {
        let response = await fetch('/bff/session', { credentials: 'include' });
        if (!response.ok) {
            throw new Error(`Unable to restore BFF session (${response.status})`);
        }
        let session = await response.json();

        if (!session.authenticated && appToken) {
            const bootstrapResponse = await fetch('/bff/login/bootstrap', {
                body: JSON.stringify({ access_token: appToken }),
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                method: 'POST',
            });
            if (!bootstrapResponse.ok) {
                throw new Error(`Unable to bootstrap BFF session (${bootstrapResponse.status})`);
            }
            response = await fetch('/bff/session', { credentials: 'include' });
            if (!response.ok) {
                throw new Error(`Unable to restore bootstrapped BFF session (${response.status})`);
            }
            session = await response.json();
        }

        store.commit('SET_AUTH_STATE', { authenticated: Boolean(session.authenticated), session });
    } catch (error) {
        // Session restoration failures need to be visible without preventing the login page from loading.
        Sentry.captureException(error);
        store.commit('SET_AUTH_STATE', { authenticated: false, session: null });
    }
}

Axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        // Rate limiting does not invalidate the session, even when the response mentions a JWT.
        if (error?.response?.status === 429) {
            EventBus.$emit('ERROR', { Message: i18n.t('errors.tooManyRequests') });
            return Promise.reject(error);
        }
        const message = String(error?.response?.data?.message || '')
            .trim()
            .toLowerCase();
        const missingCsrfToken = message.includes('missing csrf token in request header');
        const originalRequest = error?.config;

        // if missingCsrfToken try to refrsehs csrf
        if (missingCsrfToken && originalRequest && !originalRequest._csrfRetry) {
            originalRequest._csrfRetry = true;

            try {
                const refreshResult = await refreshCsrfToken();
                if (refreshResult.ok) {
                    return Axios(originalRequest);
                }
            } catch (refreshError) {
                // Fall through to the normal auth handling below when the session refresh fails.
            }
        }

        // of jwt is invalid logout user
        const isBffAuthenticationRequest = String(originalRequest?.url || '').includes('/bff/');
        if (
            !isBffAuthenticationRequest &&
            (error?.response?.status === 401 ||
                message === 'invalid or expired jwt' ||
                message === 'missing or malformed jwt')
        ) {
            // API requests can fail together while a session is being refreshed. Re-check the BFF once
            // instead of racing every individual 401 to the login route.
            await restoreBffSession();
            if (!store.getters.isAuthenticated) {
                store.commit('LOGOUT');
                // The SaaS router has no login page; keep creation errors on its current route.
                if (!isSaaSHostname(window.location.hostname) && window.location.pathname !== '/login') {
                    userrouter.push('/login');
                }
            }
            return Promise.reject(error);
        }
        return Promise.reject(error);
    }
);

/*
  capitalize
  Params: word [String]
  returns String
  converts first letter to uppercase
*/
Vue.filter('capitalize', (word) => {
    if (!word) return '';
    word = word.toString();
    return word.charAt(0).toUpperCase() + word.slice(1);
});
/*
  readabledate
  Params: timestamp [Integer]
  Returns human readable date string
*/
Vue.filter('readabledate', (value) => {
    let time = 0;

    if (typeof value === 'string') {
        time = parseInt(value);
    } else {
        time = value;
    }

    if (!value || !time) {
        return '';
    }
    time = time; // convert to milliseconds
    return moment(time).calendar(null, {
        sameDay: '[Today]',
        nextDay: '[Tomorrow]',
        nextWeek: 'dddd',
        lastDay: '[Yesterday]',
        lastWeek: '[Last] dddd',
        sameElse: 'dddd, MMM Do',
    });
});

/*
  readabletime
  Params: timestamp [Integer]
  Returns human readable time string
*/
Vue.filter('readabletime', (value) => {
    if (!parseInt(value)) return '';
    return moment(parseInt(value)).format('HH:mm');
});

/*
  timestampToString
  Params: timestamp [Integer]
  Returns human readable time string in fromat: 'YYYY-MM-DD HH:mm'
*/
Vue.filter('timestampToString', (value) => {
    if (!parseInt(value, 10)) return '';
    const timestamp = parseInt(value, 10) * 1000; // convert to milliseconds
    return moment(timestamp).format('HH:mm, DD.MM.YYYY');
});

/*
  save current viewport height in CSS variable '--viewport-height'
  needed for mobile views and software keyboard handling
*/
// Set the initial visible height too: Safari can load with its browser toolbar already expanded.
const updateViewportHeight = () => {
    const height = window.visualViewport?.height || window.innerHeight;
    document.documentElement.style.setProperty('--viewport-height', `${height}px`);
};
updateViewportHeight();
window.visualViewport?.addEventListener('resize', updateViewportHeight);
// initiate Vue

// Vue.config.productionTip = false;

const { hostname } = window.location;

const isSaaSHostname = (currentHostname: string) =>
    ['app.coapp.io', 'staging-app.coapp.io', 'app.localhost', 'staging-app.localhost'].includes(currentHostname);

const router = () => {
    let routes;
    // make sure to use the saas router explicitly for app.coapp.io and staging-app.coapp.io
    // as well as for local development with subdomains app.localhost:8080 and staging-app.localhost:8080
    // all other hostnames use the userrouter
    if (isSaaSHostname(hostname)) {
        routes = saasrouter;
    } else {
        routes = userrouter;
    }
    return routes;
};

// Wait for one BFF-owned session decision before route guards or API requests run.
restoreBffSession().finally(() => {
    new Vue({
        i18n,
        store,
        router: router(),
        render: (h) => h(App),
    }).$mount('#app');
});
